import {
  Box,
  Button,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Radio,
  RadioGroup,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Chrome, ChromeInputType, hsvaToHex, Swatch } from '@uiw/react-color';
import React from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';

import { DEFAULT_TOAST_DURATION, ToastTypes } from '@/constants/defaults';
import { ColorMode, ColorPickerState } from '@/types/ui.types';

import styles from './styles.module.scss';

interface ColorPickerDialogProps {
  colorPickerState: ColorPickerState;
  headerText: string;
  onClose: () => void;
  onClear: () => void;
  onConfirm: () => void;
  onStateChange: (colorPickerState: ColorPickerState) => void;
  isOpen?: boolean;
}

const DEFAULT_CHROME_COLOR = '#000000'; // Default color for the Chrome color picker
const DEFAULT_INPUT_TYPE = 'hexa' as ChromeInputType;
const DEFAULT_PREVIEW_TEXT = ''; // Updated to hide headerText by default
const DEFAULT_SPACE = 10;
const DEFAULT_SWATCHES_HEIGHT = 290;
const DEFAULT_SWATCHES_WIDTH = 270;
const SWATCH_RECTANGLE_HEIGHT = 24;
const SWATCH_RECTANGLE_WIDTH = 40;

const COLOR_COPY_SUCCESS_MESSAGE = "Color's HEX code copied successfully.";
const COLOR_COPY_ERROR_MESSAGE = "An error occurred when trying to copy color's HEX code.";

const COLOR_SWATCH_HEX_CODES = [
  // Reds
  '#B71C1C', '#D32F2F', '#F44336', '#E57373', '#FFCDD2',
  // Pinks
  '#880E4F', '#C2185B', '#E91E63', '#F06292', '#F8BBD0',
  // Purples
  '#4A148C', '#7B1FA2', '#9C27B0', '#BA68C8', '#E1BEE7',
  // Blues
  '#1A237E', '#303F9F', '#3F51B5', '#7986CB', '#C5CAE9',
  '#0D47A1', '#1976D2', '#2196F3', '#64B5F6', '#BBDEFB',
  // Teals
  '#006064', '#00796B', '#009688', '#4DB6AC', '#B2DFDB',
  // Greens
  '#194D33', '#388E3C', '#4CAF50', '#81C784', '#C8E6C9',
  // Yellow-Greens
  '#33691E', '#689F38', '#8BC34A', '#AED581', '#DCEDC8',
  // Yellows
  '#827717', '#AFB42B', '#CDDC39', '#DCE775', '#F0F4C3',
  // Oranges
  '#F57F17', '#FBC02D', '#FFEB3B', '#FFF176', '#FFF9C4',
  '#FF6F00', '#FFA000', '#FFC107', '#FFD54F', '#FFECB3',
  // Reds-Oranges
  '#E65100', '#F57C00', '#FF9800', '#FFB74D', '#FFE0B2',
  // Browns
  '#3E2723', '#5D4037', '#795548', '#A1887F', '#D7CCC8',
  // Grays
  '#000000', '#969696', '#D9D9D9', '#FFFFFF',
];

const ColorPickerDialog = ({
  colorPickerState,
  headerText = DEFAULT_PREVIEW_TEXT,
  isOpen = false,
  onClear,
  onClose,
  onConfirm,
  onStateChange,
}: ColorPickerDialogProps): React.JSX.Element => {
  const { cellColor, textColor, colorMode } = colorPickerState;

  const toast = useToast();

  const handleSetTextColor = (textColor: string) => {
    onStateChange({ ...colorPickerState, textColor, textColorChanged: true });
  };

  const handleSetCellColor = (cellColor: string) => {
    onStateChange({ ...colorPickerState, cellColor, cellColorChanged: true });
  };

  const handleSetColorMode = (colorMode: ColorMode) => {
    onStateChange({ ...colorPickerState, colorMode });
  };

  const handleSetColor = (color: string) => {
    if (colorMode === ColorMode.Cell) {
      handleSetCellColor(color);
    } else {
      handleSetTextColor(color);
    }
  };

  const getColorToCopy = () => {
    if (colorMode === ColorMode.Cell) {
      return cellColor;
    }
    return textColor;
  };

  const handleCopyColor = (text: string, result: boolean) => {
    if (text && result) {
      toast({
        duration: DEFAULT_TOAST_DURATION,
        isClosable: true,
        position: 'top',
        status: ToastTypes.INFO,
        title: COLOR_COPY_SUCCESS_MESSAGE,
      });
    } else {
      toast({
        duration: DEFAULT_TOAST_DURATION,
        isClosable: true,
        position: 'top',
        status: ToastTypes.ERROR,
        title: COLOR_COPY_ERROR_MESSAGE,
      });
    }
  };

  return (
    <Modal size="lg" isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false} returnFocusOnClose={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalBody>
          <HStack align={'left'} gap={DEFAULT_SPACE} position={'relative'} top={'40px'}>
            <Box
              display="flex"
              justifyContent="center"
              backgroundColor={cellColor || 'transparent'} // Transparent background if no cell color
              borderRadius={'10px'}
              alignItems={'center'}
              padding={'5px'}
              minW="40%"
              maxW="50%"
              maxH="60px"
              height="auto"
            >
              {textColor && ( // Only show headerText if textColor is selected
                <Text color={textColor} textAlign={'center'}>
                  {headerText}
                </Text>
              )}
            </Box>

            <VStack align={'left'}>
              <FormLabel>Color Picker Mode</FormLabel>
              <RadioGroup value={colorMode} onChange={(mode: ColorMode) => handleSetColorMode(mode)}>
                <HStack align={'left'} justifyContent={'left'} alignItems={'space-between'}>
                  <Radio value={ColorMode.Cell}>Cell</Radio>
                  <Radio value={ColorMode.Text}>Text</Radio>
                </HStack>
              </RadioGroup>
            </VStack>
          </HStack>
          <HStack align={'top'} marginTop={'50px'}>
            <Chrome
              color={
                colorMode === ColorMode.Cell ? cellColor || DEFAULT_CHROME_COLOR : textColor || DEFAULT_CHROME_COLOR
              } // Default to black if no color is selected
              onChange={(color) => handleSetColor(color.hex)}
              inputType={DEFAULT_INPUT_TYPE}
            />
            <Swatch
              className={styles.swatch}
              colors={COLOR_SWATCH_HEX_CODES}
              rectProps={{
                style: {
                  alignItems: 'center',
                  display: 'flex',
                  height: SWATCH_RECTANGLE_HEIGHT,
                  justifyContent: 'center',
                  width: SWATCH_RECTANGLE_WIDTH,
                },
              }}
              style={{ height: DEFAULT_SWATCHES_HEIGHT, overflowY: 'scroll', width: DEFAULT_SWATCHES_WIDTH }}
              onChange={(color) => handleSetColor(hsvaToHex(color))}
            />
          </HStack>
        </ModalBody>
        <ModalFooter justifyContent={'flex-start'}>
          <HStack justifyContent={'space-between'} width="100%">
            <HStack>
              <CopyToClipboard text={getColorToCopy()} onCopy={handleCopyColor}>
                <Button variant="ghost" colorScheme={'blue'}>
                  Copy
                </Button>
              </CopyToClipboard>

              <Button variant="ghost" onClick={onClear}>
                Clear
              </Button>
            </HStack>
            <HStack>
              <Button colorScheme={'red'} onClick={onClose}>
                Discard
              </Button>
              <Button colorScheme={'blue'} onClick={onConfirm}>
                Confirm
              </Button>
            </HStack>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ColorPickerDialog;
